/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/utils/constants.ts
 */

import type { GoogleServiceAccountKey } from '@repo/common';
import type { Constants } from '@repo/common/types/Constants';
import { desktopFeatures, webFeatures, type Feature } from '@repo/common/types/Features';
import { PLATFORM } from 'virtual:platform';
import pkg from '../../package.json';

const isServer = typeof window === 'undefined';

const NODE_ENV = isServer ? process.env.NODE_ENV || '' : import.meta.env.NODE_ENV || '';
// Override the API_SCHEME_HOST if VITE_VERCEL_URL is set
const VITE_CHANNEL = isServer ? process.env.VITE_CHANNEL || '' : import.meta.env.VITE_CHANNEL || '';

const TAURI_ENV_PLATFORM = isServer
  ? process.env.TAURI_ENV_PLATFORM || ''
  : import.meta.env.TAURI_ENV_PLATFORM || '';

let accountFeatures: Feature[] = [];

let platform: 'web' | 'windows' | 'macos' | 'ios' = PLATFORM;
let isDesktopPlatform = ['macos', 'windows'].includes(PLATFORM);

switch (platform) {
  case 'web':
    accountFeatures = webFeatures;
    break;
  case 'windows':
    accountFeatures = desktopFeatures;
    break;
  default:
    accountFeatures = desktopFeatures;
    break;
}

export const SYSTEM_BASED = 'system-based';
export const TASK_BASED = 'task-based';

export const FEATURE_FLAGS = {
  'report-slides': false,
  'new-project': false,
  'custom-api-endpoint': VITE_CHANNEL !== 'beta',
  'draft-button': true,
  'drive-tab': true,
  'web-app': true,
  'verify-sync-diagnostic': false,
  'multi-tab': false,
  'menu-add-to-favorites': true,
  'menu-duplicate': false,
  'menu-turn-into': isDesktopPlatform,
  'menu-move-to': false,
  'onboarding-flow': VITE_CHANNEL !== 'beta' ? SYSTEM_BASED : SYSTEM_BASED,
  accountFeatures
};

const API_SCHEME_HOST = isServer
  ? process.env.API_SCHEME_HOST || 'http://localhost:3000'
  : import.meta.env.VITE_API_SCHEME_HOST || 'http://localhost:3000';

const CLIENT_ID = isServer
  ? process.env.CLIENT_ID || 'UNKNOWN'
  : import.meta.env.VITE_CLIENT_ID || 'UNKNOWN';

const RETAIN_SERVICE_KEY = isServer ? process.env.RETAIN_SERVICE_KEY || '' : '';

const POSTMARK_API_KEY = isServer ? process.env.POSTMARK_API_KEY || '' : '';

const STRIPE_API_KEY = isServer ? process.env.STRIPE_API_KEY || '' : '';
const VITE_PUBLISHABLE_STRIPE_KEY = isServer
  ? process.env.VITE_PUBLISHABLE_STRIPE_KEY || ''
  : import.meta.env.VITE_PUBLISHABLE_STRIPE_KEY || '';
const STRIPE_WEBHOOK_SECRET = isServer ? process.env.STRIPE_WEBHOOK_SECRET || '' : '';

// APN (Apple Push Notification) configuration
const APN_CERT_B64 = isServer ? process.env.APN_CERT_B64 : undefined;
const APN_KEY_B64 = isServer ? process.env.APN_KEY_B64 : undefined;
const APN_TOKEN_KEY_B64 = isServer ? process.env.APN_TOKEN_KEY_B64 : undefined;
const APN_TOKEN_KEY_ID = isServer ? process.env.APN_TOKEN_KEY_ID : undefined;
const APN_TOKEN_TEAM_ID = isServer ? process.env.APN_TOKEN_TEAM_ID : undefined;
const APN_PRODUCTION = isServer ? process.env.APN_PRODUCTION === 'true' : false;
const APN_TOPIC = isServer
  ? process.env.APN_TOPIC || 'com.cozyintellect.retain'
  : 'com.cozyintellect.retain';

// Decode base64 APN credentials
let APN_CERT: string | undefined;
let APN_KEY: string | undefined;
let APN_TOKEN_KEY: string | undefined;

// Certificate-based auth
if (isServer && APN_CERT_B64 && APN_KEY_B64) {
  try {
    APN_CERT = Buffer.from(APN_CERT_B64, 'base64').toString('utf-8');
    APN_KEY = Buffer.from(APN_KEY_B64, 'base64').toString('utf-8');
  } catch (error) {
    console.error('Error decoding APN certificate credentials:', error);
    APN_CERT = undefined;
    APN_KEY = undefined;
  }
}

// Token-based auth (.p8)
if (isServer && APN_TOKEN_KEY_B64) {
  try {
    APN_TOKEN_KEY = Buffer.from(APN_TOKEN_KEY_B64, 'base64').toString('utf-8');
  } catch (error) {
    console.error('Error decoding APN token key:', error);
    APN_TOKEN_KEY = undefined;
  }
}

const ALLOWED_ENDPOINTS = [
  'http://localhost:3000',
  'https://retainapp.ai',
  'https://beta.retainapp.ai',
  'https://alpha.retainapp.ai',
  'https://web-dev.retainapp.ai',
  'https://retain.cozyintellect.com',
  API_SCHEME_HOST
];

let GCP_SERVICE_ACCOUNT_KEY: GoogleServiceAccountKey | undefined;
let MAIN_GCP_SERVICE_ACCOUNT_KEY: GoogleServiceAccountKey | undefined;

if (isServer) {
  const GCP_SERVICE_ACCOUNT_KEY_B64 = process.env.GCP_SERVICE_ACCOUNT_KEY_B64;

  if (!GCP_SERVICE_ACCOUNT_KEY_B64) {
    if (CLIENT_ID !== 'retain.macos') {
      console.warn('GCP_SERVICE_ACCOUNT_KEY is not set');
    }
    GCP_SERVICE_ACCOUNT_KEY = undefined;
  } else if (GCP_SERVICE_ACCOUNT_KEY_B64 === 'dummy-key') {
    GCP_SERVICE_ACCOUNT_KEY = undefined;
  } else {
    const jsonStr = Buffer.from(GCP_SERVICE_ACCOUNT_KEY_B64, 'base64').toString('utf-8');
    GCP_SERVICE_ACCOUNT_KEY = JSON.parse(jsonStr);
  }

  const MAIN_GCP_SERVICE_ACCOUNT_KEY_B64 = process.env.MAIN_GCP_SERVICE_ACCOUNT_KEY_B64;

  if (!MAIN_GCP_SERVICE_ACCOUNT_KEY_B64) {
    if (CLIENT_ID !== 'retain.macos') {
      console.warn('MAIN_GCP_SERVICE_ACCOUNT_KEY is not set');
    }
    MAIN_GCP_SERVICE_ACCOUNT_KEY = undefined;
  } else if (MAIN_GCP_SERVICE_ACCOUNT_KEY_B64 === 'dummy-key') {
    MAIN_GCP_SERVICE_ACCOUNT_KEY = undefined;
  } else {
    const jsonStr = Buffer.from(MAIN_GCP_SERVICE_ACCOUNT_KEY_B64, 'base64').toString('utf-8');
    MAIN_GCP_SERVICE_ACCOUNT_KEY = JSON.parse(jsonStr);
  }
} else {
  GCP_SERVICE_ACCOUNT_KEY = undefined;
}

const ENVIRONMENT = isServer
  ? process.env.ENVIRONMENT || 'production'
  : import.meta.env.VITE_ENVIRONMENT || 'production';

const IS_CICD_RAW = isServer
  ? process.env.IS_CICD || (import.meta as any)?.env?.VITE_IS_CICD
  : false;

const parseBoolean = (value: string | boolean | undefined): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true' || value === '1';
  return false;
};

const constants: Constants = {
  IS_CICD: parseBoolean(IS_CICD_RAW),
  // API scheme and host
  API_SCHEME_HOST: API_SCHEME_HOST,
  ALLOWED_ENDPOINTS: ALLOWED_ENDPOINTS,
  GATE_ACCESS: API_SCHEME_HOST === 'https://retainapp.ai',
  IS_DEBUG:
    API_SCHEME_HOST == 'http://localhost:3000' ||
    API_SCHEME_HOST == 'https://retain.cozyintellect.com' ||
    API_SCHEME_HOST == 'https://alpha.retainapp.ai' ||
    NODE_ENV === 'development',
  // Google OAuth-related constants
  AUTH_GOOGLE_ID: isServer
    ? process.env.AUTH_GOOGLE_ID
    : import.meta.env.VITE_AUTH_GOOGLE_ID || undefined,
  AUTH_REDIRECT_URI: isServer
    ? process.env.AUTH_REDIRECT_URI
    : import.meta.env.VITE_AUTH_REDIRECT_URI || undefined,
  AUTH_CALENDAR_REDIRECT_URI: `${API_SCHEME_HOST}/calendar/callback/google`,
  GOOGLE_CALENDAR_SCOPES: ['https://www.googleapis.com/auth/calendar.readonly'],
  GMAIL_SCOPES: [
    'https://www.googleapis.com/auth/gmail.readonly',
    'https://www.googleapis.com/auth/gmail.compose'
  ],
  GOOGLE_DRIVE_SCOPES: ['https://www.googleapis.com/auth/drive.file'],
  AUTH_FIREBASE_PRIVATE_KEY: isServer ? process.env.AUTH_FIREBASE_PRIVATE_KEY : undefined,
  AUTH_FIREBASE_CLIENT_EMAIL: isServer ? process.env.AUTH_FIREBASE_CLIENT_EMAIL : undefined,

  // API URL for server and client
  API_URL: `${API_SCHEME_HOST}/api`,
  RAG_API_URL: isServer ? process.env.RAG_API_URL || '' : '',

  // API-specific constants
  API_SEND_TIMEOUT: 5000,

  // Authentication token key names (for client use)
  AUTH_TOKEN_KEY_NAME: 'AUTH_TOKEN',
  REF_TOKEN_KEY_NAME: 'REF_TOKEN',

  // Limits for workspaces and API usage
  WORKSPACE_LIMIT: 5,
  API_LIMIT: 5,
  WAITLIST_URL: 'https://forms.gle/o1VHvZ5tZ6Q9cT5x6',
  CLIENT_ID: CLIENT_ID,
  CLIENT_IDS: ['retain.macos', 'retain.web'],

  AI_PROXY_URL: isServer
    ? process.env.VITE_AI_PROXY_URL || ''
    : import.meta.env.VITE_AI_PROXY_URL || '',
  POSTMARK_API_KEY: POSTMARK_API_KEY,
  PROXY_TTL_SECONDS: 3600,
  PROXY_MODEL: 'gpt-4.1',
  PROXY_MODEL_MINI: 'gpt-4.1-mini',
  RETAIN_SERVICE_KEY: RETAIN_SERVICE_KEY,
  ENVIRONMENT: ENVIRONMENT,
  PUBLIC_CLOUD_STORE_BUCKET: 'retain-media.retainapp.ai',
  CLOUD_STORE_BUCKET: isServer
    ? process.env.CLOUD_STORE_BUCKET || 'retain-storage-dev'
    : import.meta.env.VITE_CLOUD_STORE_BUCKET || 'retain-storage-dev',
  PACKAGE_VERSION: pkg.version,
  GCP_PROJECT_ID: 'retain-user-460302', // GCP project ID for user secrets
  MAIN_GCP_PROJECT_ID: 'retain-441417', // GCP project ID for main project
  MAIN_GCP_SERVICE_ACCOUNT_KEY: MAIN_GCP_SERVICE_ACCOUNT_KEY,
  GCP_SERVICE_ACCOUNT_KEY: GCP_SERVICE_ACCOUNT_KEY,
  SECRET_PREFIX: ENVIRONMENT === 'production' ? 'prod_user_' : 'dev_user_',
  KMS_KEY_NAME: isServer
    ? process.env.KMS_KEY_NAME ||
    'projects/retain-441417/locations/us-west1/keyRings/app-secrets/cryptoKeys/oauth-token-key'
    : '',
  TIMEOUT_SHORT: 5000,
  TIMEOUT_MEDIUM: 8000,
  TIMEOUT_LONG: 15000,
  TIMEOUT_XL: 30000,
  TIMEOUT_SIZE: {
    short: 5000,
    medium: 8000,
    long: 15000,
    xl: 30000,
    '2xl': 60000,
    '3xl': 120000,
    '4xl': 240000,
    '5xl': 480000,
    '6xl': 960000
  },
  STRIPE_API_KEY: STRIPE_API_KEY,
  STRIPE_API_VERSION: '2025-08-27.basil',
  VITE_PUBLISHABLE_STRIPE_KEY: VITE_PUBLISHABLE_STRIPE_KEY,
  STRIPE_WEBHOOK_SECRET: STRIPE_WEBHOOK_SECRET,
  STRIPE_CUSTOMER_PORTAL_LINK: isServer
    ? process.env.STRIPE_CUSTOMER_PORTAL_LINK || ''
    : import.meta.env.VITE_STRIPE_CUSTOMER_PORTAL_LINK || '',
  STRIPE_PAYMENT_LINK_PRO_MONTHLY: isServer
    ? process.env.STRIPE_PAYMENT_LINK_PRO_MONTHLY || ''
    : import.meta.env.VITE_STRIPE_PAYMENT_LINK_PRO_MONTHLY || '',
  STRIPE_PAYMENT_LINK_PRO_ANUALLY: isServer
    ? process.env.STRIPE_PAYMENT_LINK_PRO_ANUALLY || ''
    : import.meta.env.VITE_STRIPE_PAYMENT_LINK_PRO_ANUALLY || '',
  DOWNLOAD_URL_MACOS: `https://releases.retainapp.ai/download/retain/retain/latest/platform/dmg-aarch64?channel=${VITE_CHANNEL}`,
  DOWNLOAD_URL_WINDOWS: `https://releases.retainapp.ai/download/retain/retain/latest/platform/windows-x86_64?channel=${VITE_CHANNEL}`,
  VITE_VERCEL_URL: isServer
    ? process.env.VITE_VERCEL_URL || ''
    : import.meta.env.VITE_VERCEL_URL || '',
  SANITY_API_DATASET: isServer
    ? process.env.VITE_SANITY_DATASET || ''
    : import.meta.env.VITE_SANITY_DATASET || '',
  SANITY_API_PROJECT_ID: isServer
    ? process.env.VITE_SANITY_PROJECT_ID || ''
    : import.meta.env.VITE_SANITY_PROJECT_ID || '',
  BYPASS_AUTH: isServer
    ? parseBoolean(process.env.BYPASS_AUTH)
    : parseBoolean(import.meta.env.VITE_BYPASS_AUTH),
  // @ts-ignore - __PLATFORM__ is defined at build time by vite/rollup
  PLATFORM: platform as 'macos' | 'web' | 'ios' | 'windows' | 'desktop',
  IS_DESKTOP: isDesktopPlatform,
  CHANNEL: VITE_CHANNEL,
  APN_CERT: APN_CERT,
  APN_KEY: APN_KEY,
  APN_TOKEN_KEY: APN_TOKEN_KEY,
  APN_TOKEN_KEY_ID: APN_TOKEN_KEY_ID,
  APN_TOKEN_TEAM_ID: APN_TOKEN_TEAM_ID,
  APN_PRODUCTION: APN_PRODUCTION,
  APN_TOPIC: APN_TOPIC
};

/**
 * Generates an external retain link.
 * @returns {string} The retain link with a properly encoded callback URL.
 */
export function getExternalRetainSignInLink(callbackPath = '/protected'): string {
  const endpoint = constants.VITE_VERCEL_URL
    ? `${constants.VITE_VERCEL_URL.includes('localhost') || constants.VITE_VERCEL_URL.includes('192') ? 'http' : 'https'}://${constants.VITE_VERCEL_URL}`
    : constants.API_SCHEME_HOST;
  const callbackUrl = `${endpoint}${callbackPath}`;
  return `${endpoint}/sign-in?callbackUrl=${encodeURIComponent(callbackUrl)}`;
}

export function getExternalRetainSignUpLink(): string {
  const endpoint = constants.VITE_VERCEL_URL
    ? `${constants.VITE_VERCEL_URL.includes('localhost') || constants.VITE_VERCEL_URL.includes('192') ? 'http' : 'https'}://${constants.VITE_VERCEL_URL}`
    : constants.API_SCHEME_HOST;
  const callbackUrl = `${endpoint}/protected`;
  return `${endpoint}/sign-up?callbackUrl=${encodeURIComponent(callbackUrl)}`;
}

/**
 * Converts a Google OAuth client ID to the reversed format used for custom schemes.
 * @param clientId - The Google OAuth client ID (e.g., "370447212804-2bh3ph1e3ibjvqk0c0cvqdtb3oi0ii2q.apps.googleusercontent.com")
 * @returns The reversed format scheme
 */
export function getGoogleOAuthScheme(clientId: string): string {
  // The clientId already contains "apps.googleusercontent.com" at the end
  // We need to extract the numeric part and create the reversed scheme
  const numericPart = clientId.replace('.apps.googleusercontent.com', '');
  return `com.googleusercontent.apps.${numericPart}`;
}

export function getGoogleCalendarAuthLink(calendar: boolean = true, mail: boolean = true): string {
  const scopes: string[] = [];
  if (calendar) {
    scopes.push(...constants.GOOGLE_CALENDAR_SCOPES);
  }
  if (mail) {
    scopes.push(...constants.GMAIL_SCOPES);
  }

  // Gets the calendar access authorization link
  const endpoint = constants.VITE_VERCEL_URL
    ? `${constants.VITE_VERCEL_URL.includes('localhost') || constants.VITE_VERCEL_URL.includes('192') ? 'http' : 'https'}://${constants.VITE_VERCEL_URL}`
    : constants.API_SCHEME_HOST;

  const callbackUrl = `${endpoint}/calendar/success`;

  return `${endpoint}/calendar/redirect/google?scopes=${encodeURIComponent(scopes.join(','))}&callbackUrl=${encodeURIComponent(callbackUrl)}`; // Use goto to navigate
}

export function getGoogleEmailAuthLink(): string {
  const scopes = [...constants.GMAIL_SCOPES];

  // Gets the email access authorization link
  const endpoint = constants.VITE_VERCEL_URL
    ? `${constants.VITE_VERCEL_URL.includes('localhost') || constants.VITE_VERCEL_URL.includes('192') ? 'http' : 'https'}://${constants.VITE_VERCEL_URL}`
    : constants.API_SCHEME_HOST;

  const callbackUrl = `${endpoint}/email/success`;

  return `${endpoint}/calendar/redirect/google?scopes=${encodeURIComponent(scopes.join(','))}&callbackUrl=${encodeURIComponent(callbackUrl)}`;
}

export function getGoogleDriveAuthLink(): string {
  const scopes = [...constants.GOOGLE_DRIVE_SCOPES];

  // Gets the Google Drive access authorization link
  const endpoint = constants.VITE_VERCEL_URL
    ? `${constants.VITE_VERCEL_URL.includes('localhost') || constants.VITE_VERCEL_URL.includes('192') ? 'http' : 'https'}://${constants.VITE_VERCEL_URL}`
    : constants.API_SCHEME_HOST;

  const callbackUrl = `${endpoint}/docs/success`;

  return `${endpoint}/calendar/redirect/google?scopes=${encodeURIComponent(scopes.join(','))}&callbackUrl=${encodeURIComponent(callbackUrl)}`;
}

export function isCICD(): boolean {
  return constants.IS_CICD === true;
}

/**
 * Converts an avatar URL to use the CDN format
 * @param avatarUrl - The avatar URL (can be old storage.googleapis.com format or just filename)
 * @returns The CDN URL (e.g., 'https://retain-media.retainapp.ai/avatars/user123.jpg')
 */
export function getAvatarCdnUrl(avatarUrl: string): string {
  // If it contains 'avatars/', extract everything from that point
  if (avatarUrl.includes('avatars/')) {
    const avatarIndex = avatarUrl.indexOf('avatars/');
    const avatarPath = avatarUrl.substring(avatarIndex);
    return `https://retain-media.retainapp.ai/${avatarPath}`;
  } else {
    return avatarUrl;
  }
}

export default constants;

if (isServer) {
  console.log('Loaded constants:', {
    API_SCHEME_HOST: constants.API_SCHEME_HOST,
    NODE_ENV: NODE_ENV,
    CHANNEL: constants.CHANNEL,
    CLIENT_ID: constants.CLIENT_ID,
    PLATFORM: constants.PLATFORM,
    ENVIRONMENT: constants.ENVIRONMENT,
    IS_CICD: constants.IS_CICD,
    VITE_VERCEL_URL: constants.VITE_VERCEL_URL,
    AI_PROXY_URL: constants.AI_PROXY_URL,
    CLOUD_STORE_BUCKET: constants.CLOUD_STORE_BUCKET,
    RAG_API_URL: constants.RAG_API_URL,
    AUTH_REDIRECT_URI: constants.AUTH_REDIRECT_URI,
    FEATURE_FLAGS: FEATURE_FLAGS,
    TAURI_ENV_PLATFORM: TAURI_ENV_PLATFORM,
    STRIPE_API_KEY: STRIPE_API_KEY.slice(0, 3) + '...',
    STRIPE_CUSTOMER_PORTAL_LINK: constants.STRIPE_CUSTOMER_PORTAL_LINK,
    STRIPE_PAYMENT_LINK_PRO_MONTHLY: constants.STRIPE_PAYMENT_LINK_PRO_MONTHLY,
    STRIPE_PAYMENT_LINK_PRO_ANUALLY: constants.STRIPE_PAYMENT_LINK_PRO_ANUALLY
  });

}

const isWindows = constants.PLATFORM === 'windows';
export const separator = isWindows ? '\\' : '/';
